//ModernDashboard.tsx
'use client';

import { useEffect, useRef, useState } from 'react';
import Split from 'react-split';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LineChart,
  Eye,
  ArrowUpDown,
  Settings,
  Menu,
  FileText,
  Lock,
  Code,
  Shield,
  ShoppingBag,
  MessageCircle,
  Info,
  User,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/hooks/useAuth';
import { Transactions } from '@/components/dashboard/Transactions';
import { DashboardWatchlist } from '@/components/dashboard/DashboardWatchlist';
import Image from 'next/image';
import LoadingLogo from '@/components/component/loading-screen/animated-logo';
import { useDashboard } from '@/contexts/DashboardContext';
import { TradingView } from '@/components/dashboard/TradingView';
import { useRouter } from 'next/navigation';
import Link from 'next/link';

interface ModernDashboardProps {
  onLoadComplete?: () => void;
  isTvLibraryReady?: boolean;
}

export function ModernDashboard({
  onLoadComplete,
  isTvLibraryReady = false,
}: ModernDashboardProps) {
  // @ts-ignore - We know logout exists on useAuth return value
  const { user, logout } = useAuth();
  const { state, dispatch, selectedSymbol, setSelectedSymbolInfo } =
    useDashboard();
  const router = useRouter();

  const [isMobile, setIsMobile] = useState(false);
  const [activeView, setActiveView] = useState<
    'chart' | 'transactions' | 'watchlist'
  >(state.viewMode);
  const [isLoading, setIsLoading] = useState(true);
  const [tvWidget, setTvWidget] = useState<any>(null);
  const [activeChart, setActiveChart] = useState<'tradingview' | 'recharts'>(
    'tradingview'
  );

  // Split panel sizes - Updated for better proportions
  const [watchlistWidth, setWatchlistWidth] = useState(22); // Reduced from 25% to 22% for better layout
  const [mainSplitSizes, setMainSplitSizes] = useState([70, 30]); // Improved chart to transactions ratio
  const [verticalSplitSizes, setVerticalSplitSizes] = useState([65, 35]); // Give more space to transactions (35% instead of 30%)

  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const handleMouseEnter = (path: string, event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      top: rect.top + rect.height / 2,
      left: rect.right + 8
    });
    setHoveredItem(path);
  };

  const handleChartTypeChange = (chartType: 'tradingview' | 'recharts') => {
    console.log('Chart type changed to:', chartType);
    setActiveChart(chartType);
  };
  // Refs for containers
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const transactionContainerRef = useRef<HTMLDivElement>(null);
  const hasUserAdjustedSplit = useRef(false);

  // Define navigation items to match Navbar
  const navigationItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: <LineChart className="h-5 w-5" />,
    },
    {
      name: 'Marketplace',
      path: '/marketplace',
      icon: <ShoppingBag className="h-5 w-5" />,
    },
    {
      name: 'Charts',
      path: '/charts',
      icon: <LineChart className="h-5 w-5" />,
    },
    {
      name: 'Blog',
      path: '/blog',
      icon: <FileText className="h-5 w-5" />,
    },
    {
      name: 'Forums',
      path: '/forums',
      icon: <MessageCircle className="h-5 w-5" />,
    },
    {
      name: 'About',
      path: '/about',
      icon: <Info className="h-5 w-5" />,
    },
  ];

  // Update your customGutter function for transparency
  const customGutter = (index, direction) => {
    // Create the gutter element
    const gutter = document.createElement('div');

    // Apply basic classes with transparent background
    gutter.className = `
    gutter gutter-${direction} 
    transition-colors duration-200 ease-in-out
    z-[1001] relative
  `;

    // Use inline styles with !important to override any existing styles
    gutter.style.cssText = `
    background-color: transparent !important;
    transition: background-color 0.2s ease-in-out !important;
    z-index: 1001 !important;
    position: relative !important;
  `;

    if (direction === 'horizontal') {
      gutter.classList.add('cursor-col-resize');
      gutter.style.cssText += `
      width: 8px !important;
      margin-left: -4px !important;
      margin-right: -4px !important;
      cursor: col-resize !important;
    `;
    } else {
      gutter.classList.add('cursor-row-resize');
      gutter.style.cssText += `
      height: 8px !important;
      margin-top: -4px !important;
      margin-bottom: -4px !important;
      cursor: row-resize !important;
    `;
    }

    // Create a minimal, nearly invisible handle
    const handle = document.createElement('div');
    handle.className = 'gutter-handle';
    handle.style.cssText = `
    position: absolute !important;
    background-color: rgba(255, 255, 255, 0.1) !important; /* Very subtle handle */
    border-radius: 0.375rem !important;
    pointer-events: none !important;
  `;

    if (direction === 'horizontal') {
      handle.style.cssText += `
      top: calc(50% - 25px) !important;
      left: 50% !important;
      transform: translateX(-50%) !important;
      height: 50px !important;
      width: 2px !important;
    `;
    } else {
      handle.style.cssText += `
      top: 50% !important;
      left: calc(50% - 25px) !important;
      transform: translateY(-50%) !important;
      height: 2px !important;
      width: 50px !important;
    `;
    }

    // Add subtle hover behavior
    gutter.addEventListener('mouseover', () => {
      handle.style.backgroundColor = 'rgba(255, 255, 255, 0.2) !important'; // Slightly more visible on hover
    });

    gutter.addEventListener('mouseout', () => {
      if (!document.body.classList.contains('splitting')) {
        handle.style.backgroundColor = 'rgba(255, 255, 255, 0.1) !important';
      }
    });

    // Add the handle to the gutter
    gutter.appendChild(handle);

    return gutter;
  };

  useEffect(() => {
    // Add style for dragging state
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
    body.splitting .gutter {
      background-color: transparent !important;
    }
    
    body.splitting .gutter .gutter-handle {
      background-color: rgba(255, 255, 255, 0.2) !important;
    }
  `;
    document.head.appendChild(styleEl);

    return () => {
      document.head.removeChild(styleEl);
    };
  }, []);

  const handleSymbolSelect = (symbolName: string) => {
    const [exchange, description] = symbolName.split(':');

    console.log(`ModernDashboard: Symbol selected: ${symbolName}`);

    // Update context state
    dispatch({ type: 'SET_SYMBOL', payload: symbolName });

    // Update selected symbol info
    setSelectedSymbolInfo({
      name: symbolName,
      description: description || symbolName,
      exchange: exchange || 'Rolex',
    });

    // IMPORTANT: If we have window.tvWidget, use it directly with the exact working pattern
    if (typeof window !== 'undefined' && window.tvWidget) {
      try {
        const currentInterval =
          window.tvWidget.symbolInterval?.()?.interval || '1D';
        console.log(
          `ModernDashboard: Using window.tvWidget to update to ${symbolName}`
        );

        // First reset symbol to force a complete reload - EXACT PATTERN from working code
        window.tvWidget.setSymbol('', currentInterval, () => {
          // Then set to new symbol with a delay of 500ms - critical!
          setTimeout(() => {
            window.tvWidget.setSymbol(symbolName, currentInterval, () => {
              console.log(
                `ModernDashboard: Chart successfully updated to ${symbolName}`
              );
            });
          }, 500);
        });
      } catch (err) {
        console.error('Error using window.tvWidget:', err);

        // Try using the global helper as fallback
        if (typeof window.forceChartSymbolUpdate === 'function') {
          window.forceChartSymbolUpdate(symbolName);
        }
      }
    }
    // If we have tvWidget from state, use that
    else if (tvWidget) {
      try {
        const currentInterval = tvWidget.symbolInterval?.()?.interval || '1D';

        // Use same pattern
        tvWidget.setSymbol('', currentInterval, () => {
          setTimeout(() => {
            tvWidget.setSymbol(symbolName, currentInterval);
          }, 500);
        });
      } catch (err) {
        console.error('Error using tvWidget from state:', err);
      }
    }
    // Last resort - use our global helper function
    else if (
      typeof window !== 'undefined' &&
      typeof window.forceChartSymbolUpdate === 'function'
    ) {
      window.forceChartSymbolUpdate(symbolName);
    }
    // Dispatch custom event as final fallback
    else {
      try {
        const event = new CustomEvent('watch-symbol-selected', {
          detail: { symbol: symbolName },
          bubbles: true,
        });
        window.dispatchEvent(event);
        document.dispatchEvent(event);
      } catch (e) {
        console.error('Error dispatching symbol event:', e);
      }
    }
  };

  // Update your forceCorrectLayout function
  // In your ModernDashboard.tsx

  // Fix the vertical split by adjusting how we handle sizes
  const forceCorrectLayout = () => {
    // Skip layout adjustments if user has manually adjusted the split
    if (hasUserAdjustedSplit.current) {
      // Only perform widget resize without adjusting layout
      if (window.tvWidget && typeof window.tvWidget.resize === 'function') {
        try {
          setTimeout(() => window.tvWidget.resize(), 100);
        } catch (err) {
          console.warn('Error in tvWidget.resize():', err);
        }
      }
      return;
    }

    // Just set minimum heights and let Split.js handle the actual sizing
    if (chartContainerRef.current && transactionContainerRef.current) {
      chartContainerRef.current.style.minHeight = '200px';
      transactionContainerRef.current.style.minHeight = '300px'; // Increased from 100px to give more space
    }

    // Notify TradingView widget of resize if available
    if (window.tvWidget && typeof window.tvWidget.resize === 'function') {
      try {
        setTimeout(() => window.tvWidget.resize(), 100);
      } catch (err) {
        console.warn('Error in tvWidget.resize():', err);
      }
    }
  };

  // Effect to get TV widget reference once available
  useEffect(() => {
    const checkTvWidget = () => {
      // This assumes the tvWidget is stored in window after initialization
      if (window.tvWidget) {
        console.log('ModernDashboard: Found TV widget reference');
        setTvWidget(window.tvWidget);

        // Log widget properties to help diagnose issues
        try {
          console.log('ModernDashboard: TV widget details:', {
            hasSymbolInterval:
              typeof window.tvWidget.symbolInterval === 'function',
            currentSymbol:
              window.tvWidget.symbolInterval?.()?.symbol || 'unknown',
            interval: window.tvWidget.symbolInterval?.()?.interval || 'unknown',
          });
        } catch (err) {
          console.warn('Could not get widget details:', err);
        }

        return true;
      }
      return false;
    };

    // Check immediately
    if (!checkTvWidget()) {
      // If not found, set up a short polling mechanism with more frequent checks
      console.log('ModernDashboard: Setting up polling for tvWidget');
      const intervalId = setInterval(() => {
        if (checkTvWidget()) {
          console.log('ModernDashboard: Found widget through polling');
          clearInterval(intervalId);
        }
      }, 500); // Poll every 500ms instead of 1000ms

      // Clean up interval after 15 seconds max (longer than before)
      const timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        console.warn('ModernDashboard: tvWidget polling timed out after 15s');
      }, 15000);

      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  // Apply force layout after component mounts - INCREASED CALLS
  useEffect(() => {
    // Only apply initial layout if user hasn't adjusted
    if (!hasUserAdjustedSplit.current) {
      const layoutTimers = [100, 500, 1000, 1500, 2000];
      layoutTimers.forEach((delay) => setTimeout(forceCorrectLayout, delay));
    }

    const handleSplitResize = () => {
      console.log('Split resize event detected');
      // Only apply layout if user hasn't adjusted
      if (!hasUserAdjustedSplit.current) {
        setTimeout(forceCorrectLayout, 100);
      } else {
        // Just resize the widget without changing layout
        if (window.tvWidget && typeof window.tvWidget.resize === 'function') {
          setTimeout(() => window.tvWidget.resize(), 100);
        }
      }
    };

    const handleWindowResize = () => {
      console.log('Window resize detected');
      setTimeout(forceCorrectLayout, 100);
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('split-resize', handleSplitResize);
    window.addEventListener('tvChartReady', handleSplitResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('split-resize', handleSplitResize);
      window.removeEventListener('tvChartReady', handleSplitResize);
    };
  }, []);

  // Update view mode in context when it changes locally
  useEffect(() => {
    dispatch({ type: 'SET_VIEW_MODE', payload: activeView });
  }, [activeView, dispatch]);

  // Effect: detect mobile view
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simulate loading completion
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
      dispatch({ type: 'SET_LOADING', payload: false });
      if (onLoadComplete) {
        onLoadComplete();
      }

      // Force layout after load complete
      forceCorrectLayout();
    }, 1500);

    return () => clearTimeout(timer);
  }, [onLoadComplete, dispatch]);

  const [showMenu, setShowMenu] = useState(false);

  if (isMobile) {
    return (
      <div className="flex h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white">
        <header className="h-16 border-b border-slate-800/80 flex items-center px-4 sticky top-0 bg-slate-950/95 backdrop-blur-md z-40 shadow-md shadow-black/20">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <Image
                src="/watchinspect.svg"
                width={28}
                height={28}
                alt="Watch Inspect Logo"
                className="relative transform transition-transform duration-200"
              />
              <div>
                <h1 className="font-bold text-lg bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-400">
                  WatchInspect
                </h1>
                <p className="text-xs text-slate-400 -mt-1">Price Tracker</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                variant="outline"
                className="bg-blue-500/10 text-blue-400 border-blue-500/30 px-2 py-1 text-xs"
              >
                {selectedSymbol?.name?.split(':')[0] || 'ROLEX'}
              </Badge>
              <Avatar className="h-8 w-8 border-2 border-blue-600/30">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-slate-800 text-blue-400">
                  {user?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </header>

        <div className="w-full bg-slate-900/80 backdrop-blur-sm p-3 border-b border-slate-800/60 shadow-sm shadow-black/10">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-md font-semibold">
                {selectedSymbol?.description || 'Submariner 16610'}
              </h2>
              <p className="text-xs text-slate-400">
                Reference: {selectedSymbol?.name?.split(':')[1] || '16610'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold">$12,500</p>
              <p className="text-xs text-green-400 flex items-center justify-end">
                +$250 (2.1%)
              </p>
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-hidden">
          <Tabs defaultValue={activeView} className="h-full">
            <TabsList className="flex justify-center bg-slate-900/80 backdrop-blur-sm mb-3 p-1.5 mx-4 rounded-xl border border-slate-800/60 shadow-inner shadow-black/15">
              <TabsTrigger
                value="chart"
                className="flex-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                onClick={() => setActiveView('chart')}
              >
                <LineChart className="h-4 w-4 mr-2" />
                Chart
              </TabsTrigger>
              <TabsTrigger
                value="watchlist"
                className="flex-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                onClick={() => setActiveView('watchlist')}
              >
                <Eye className="h-4 w-4 mr-2" />
                Models
              </TabsTrigger>
              <TabsTrigger
                value="transactions"
                className="flex-1 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                onClick={() => setActiveView('transactions')}
              >
                <ArrowUpDown className="h-4 w-4 mr-2" />
                Trades
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="chart"
              className="h-[calc(100vh-16.5rem)] m-0 outline-none p-0"
            >
              <div className="p-3 h-full">
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl shadow-lg shadow-black/20 overflow-hidden h-full border border-slate-800/40">
                  <TradingView
                    selectedSymbol={selectedSymbol?.name || 'ROLEX:16610'}
                    onSymbolChanged={handleSymbolSelect}
                    isTvLibraryReady={isTvLibraryReady}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="watchlist"
              className="h-[calc(100vh-16rem)] m-0 outline-none p-0"
            >
              <div className="p-2 h-full">
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl shadow-lg shadow-black/20 overflow-hidden h-full">
                  <DashboardWatchlist
                    selectedSymbol={selectedSymbol || { name: 'ROLEX:16610' }}
                    onSymbolSelect={handleSymbolSelect}
                    tvWidget={tvWidget}
                    activeChart={activeChart}
                    onChartTypeChange={handleChartTypeChange}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="transactions"
              className="h-[calc(100vh-16rem)] m-0 outline-none p-0"
            >
              <div className="p-2 h-full">
                <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl shadow-lg shadow-black/20 overflow-hidden h-full">
                  <Transactions
                    selectedSymbol={selectedSymbol || { name: 'ROLEX:16610' }}
                    mobile={true}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </main>

        <div className="px-4 py-3 bg-slate-950/60 border-t border-slate-800/40">
          <div className="flex space-x-2 overflow-x-auto pb-1 scrollbar-hide">
            {['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL'].map(
              (period) => (
                <Badge
                  key={period}
                  variant="outline"
                  className={`px-3 py-1 cursor-pointer whitespace-nowrap rounded-md transition-colors duration-200
                  ${period === state.timeframe
                      ? 'bg-blue-600 text-white border-transparent shadow-sm shadow-blue-500/20'
                      : 'bg-slate-800/60 text-slate-300 border-slate-700/50 hover:bg-slate-800/80'
                    }
                `}
                  onClick={() =>
                    dispatch({
                      type: 'SET_TIMEFRAME',
                      payload: period as any,
                    })
                  }
                >
                  {period}
                </Badge>
              )
            )}
          </div>
        </div>

        <footer className="fixed bottom-0 left-0 right-0 h-16 border-t border-slate-800/80 bg-slate-950/95 backdrop-blur-lg px-2 flex items-center justify-between z-40 shadow-lg shadow-black/30">
          <div className="w-full grid grid-cols-5 gap-1">
            <Button
              variant="ghost"
              className={`flex flex-col items-center justify-center h-14 w-full rounded-xl
                ${activeView === 'chart' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'text-slate-400 hover:text-slate-300'}`}
              onClick={() => setActiveView('chart')}
            >
              <LineChart className="h-5 w-5 mb-1" />
              <span className="text-xs">Chart</span>
            </Button>

            <Button
              variant="ghost"
              className={`flex flex-col items-center justify-center h-14 w-full rounded-xl
                ${activeView === 'transactions' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'text-slate-400 hover:text-slate-300'}`}
              onClick={() => setActiveView('transactions')}
            >
              <ArrowUpDown className="h-5 w-5 mb-1" />
              <span className="text-xs">Trades</span>
            </Button>

            <Button
              variant="ghost"
              className={`flex flex-col items-center justify-center h-14 w-full rounded-xl
                ${activeView === 'watchlist' ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30' : 'text-slate-400 hover:text-slate-300'}`}
              onClick={() => setActiveView('watchlist')}
            >
              <Eye className="h-5 w-5 mb-1" />
              <span className="text-xs">Models</span>
            </Button>

            <Button
              variant="ghost"
              className="flex flex-col items-center justify-center h-14 w-full rounded-xl text-slate-400 hover:text-slate-300"
              onClick={() => router.push('/settings')}
            >
              <Settings className="h-5 w-5 mb-1" />
              <span className="text-xs">Settings</span>
            </Button>

            <Button
              variant="ghost"
              className="flex flex-col items-center justify-center h-14 w-full rounded-xl text-slate-400 hover:text-slate-300"
              onClick={() => setShowMenu(!showMenu)}
            >
              <Menu className="h-5 w-5 mb-1" />
              <span className="text-xs">More</span>
            </Button>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-white overflow-y-auto">
      <div className="w-16 border-r border-slate-800/80 bg-slate-900/90 backdrop-blur-sm z-30 shadow-lg shadow-black/30 flex-shrink-0">
        <div className="flex flex-col h-full text-slate-300">
          <div className="p-4 border-b border-slate-800 flex items-center justify-center h-14 bg-gradient-to-b from-slate-900 to-slate-900/80">
            <Link href="/" className="flex items-center justify-center">
              <Image
                src="/watchinspect.svg"
                width={28}
                height={28}
                alt="Watch Inspect Logo"
                className="relative flex-shrink-0"
              />
            </Link>
          </div>

          <div className="flex-1 overflow-y-auto scrollbar-hide py-4">
            <div className="space-y-3 px-2">
              {navigationItems.map((item) => (
                <div
                  key={item.path}
                  className="relative"
                  onMouseEnter={(e) => handleMouseEnter(item.path, e)}
                  onMouseLeave={() => setHoveredItem(null)}
                >
                  <Link
                    href={item.path}
                    className={`w-full flex justify-center px-2 py-2 rounded-lg transition-colors duration-200 ${activeView === item.path
                      ? 'bg-blue-600/20 text-blue-400 border border-blue-500/20'
                      : 'text-slate-300 hover:bg-slate-800/40 hover:text-blue-400'
                      }`}
                  >
                    {item.icon}
                  </Link>
                  <AnimatePresence>
                    {hoveredItem === item.path && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed bg-slate-800/90 text-white px-3 py-1.5 rounded-md whitespace-nowrap text-sm font-medium backdrop-blur-sm border border-slate-700/50 shadow-lg z-50"
                        style={{
                          top: tooltipPosition.top,
                          left: tooltipPosition.left,
                          transform: 'translateY(-50%)',
                        }}
                      >
                        {item.name}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          <div className="p-4 border-t border-slate-800 bg-slate-900/50">
            {user ? (
              <div
                className="relative"
                onMouseEnter={(e) => handleMouseEnter('settings', e)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <Link href="/settings">
                  <Button
                    variant="ghost"
                    className="w-full justify-center px-2 hover:bg-slate-800/40"
                  >
                    <Avatar className="h-8 w-8 border-2 border-blue-600/30">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className="bg-slate-800 text-blue-400">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </Link>
                <AnimatePresence>
                  {hoveredItem === 'settings' && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="fixed bg-slate-800/90 text-white px-3 py-1.5 rounded-md whitespace-nowrap text-sm font-medium backdrop-blur-sm border border-slate-700/50 shadow-lg z-50"
                      style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translateY(-50%)',
                      }}
                    >
                      Settings
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div
                className="relative"
                onMouseEnter={(e) => handleMouseEnter('auth', e)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <Link href="/auth">
                  <Button
                    variant="ghost"
                    className="w-full justify-center px-2 hover:bg-slate-800/40"
                  >
                    <User className="h-5 w-5" />
                  </Button>
                </Link>
                <AnimatePresence>
                  {hoveredItem === 'auth' && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.2 }}
                      className="fixed bg-slate-800/90 text-white px-3 py-1.5 rounded-md whitespace-nowrap text-sm font-medium backdrop-blur-sm border border-slate-700/50 shadow-lg z-50"
                      style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: 'translateY(-50%)',
                      }}
                    >
                      Login
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col h-screen min-w-0 overflow-y-auto">
        <header className="h-14 border-b border-slate-800/80 flex items-center justify-between px-6 bg-slate-950/95 backdrop-blur-md shadow-sm shadow-black/20">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-medium bg-clip-text text-transparent bg-gradient-to-r from-white to-blue-400">
              Dashboard
            </h1>

            <div className="flex space-x-2">
              {['1D', '1W', '1M', '3M', '6M', 'YTD', '1Y', 'ALL'].map(
                (period) => (
                  <Badge
                    key={period}
                    variant="outline"
                    className={`px-2.5 py-0.5 cursor-pointer
              ${period === state.timeframe ? 'bg-blue-600 text-white border-transparent' : 'bg-slate-800/50 text-slate-300 border-slate-700'}
            `}
                    onClick={() =>
                      dispatch({
                        type: 'SET_TIMEFRAME',
                        payload: period as any,
                      })
                    }
                  >
                    {period}
                  </Badge>
                )
              )}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <Badge
              variant="outline"
              className="bg-blue-500/10 text-blue-400 border-blue-500/30 px-2 py-1"
            >
              {selectedSymbol?.name?.split(':')[0] || 'ROLEX'}:
              {selectedSymbol?.name?.split(':')[1] || '16610'}
            </Badge>
          </div>
        </header>

        <div className="border-b border-slate-800/80 bg-gradient-to-r from-slate-900 to-slate-800/90 p-3 shadow-sm shadow-black/10">
          <div className="flex justify-between items-center max-w-6xl mx-auto">
            <div className="flex items-center space-x-4">
              <div className="bg-slate-800 rounded-full p-2 border border-slate-700/50">
                <Image
                  src={`/${(selectedSymbol?.name?.split(':')[0] || 'rolex').toLowerCase()}.svg`}
                  width={24}
                  height={24}
                  alt={selectedSymbol?.name?.split(':')[0] || 'Rolex'}
                  onError={(e) => {
                    e.currentTarget.src = '/watchinspect.svg';
                  }}
                />
              </div>

              <div>
                <h2 className="text-md font-semibold flex items-center">
                  {selectedSymbol?.description || 'Submariner 16610'}
                  <Badge className="ml-2 bg-green-500/20 text-green-400 border-0 text-xs">
                    Active
                  </Badge>
                </h2>
                <p className="text-xs text-slate-400">
                  Reference: {selectedSymbol?.name?.split(':')[1] || '16610'} •
                  Source:{' '}
                  {state.source === 'all' ? 'All Sources' : state.source}
                </p>
              </div>
            </div>

            <div className="text-right">
              <div className="flex items-center space-x-3">
                <div>
                  <p className="text-sm text-slate-300">Last Price</p>
                  <p className="text-lg font-semibold">$12,500</p>
                </div>

                <div className="bg-green-500/10 px-3 py-2 rounded-lg border border-green-500/20">
                  <p className="text-sm text-green-400 flex items-center">
                    +$250 (2.1%)
                  </p>
                  <p className="text-xs text-slate-400">vs previous day</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <Split
            className="flex h-full"
            sizes={[100 - watchlistWidth, watchlistWidth]}
            minSize={180}
            gutterSize={8}
            expandToMin={true}
            gutter={customGutter}
            onDragStart={() => {
              document.body.classList.add('splitting');
            }}
            onDragEnd={(sizes) => {
              setWatchlistWidth(sizes[1]);
              window.dispatchEvent(new CustomEvent('split-resize'));
              setTimeout(forceCorrectLayout, 100);
              document.body.classList.remove('splitting');
            }}
          >
            {/* Left panel with VERTICAL split */}
            <div className="flex flex-col min-h-0 bg-transparent">
              {/* Add vertical Split here */}
              <Split
                className="flex flex-col h-full"
                sizes={verticalSplitSizes}
                minSize={[200, 100]}
                gutterSize={8}
                direction="vertical"
                gutter={customGutter}
                onDragStart={() => {
                  document.body.classList.add('splitting');
                }}
                onDragEnd={(sizes) => {
                  // Mark that user has manually adjusted the split
                  hasUserAdjustedSplit.current = true;

                  // Update state with new sizes
                  setVerticalSplitSizes(sizes);

                  // Just resize the widget
                  if (
                    window.tvWidget &&
                    typeof window.tvWidget.resize === 'function'
                  ) {
                    try {
                      setTimeout(() => window.tvWidget.resize(), 100);
                    } catch (err) {
                      console.warn('Error resizing tvWidget:', err);
                    }
                  }

                  document.body.classList.remove('splitting');
                }}
              >
                {/* Top panel - Chart */}
                <div
                  ref={chartContainerRef}
                  className="flex flex-col p-4 pb-2 h-[65%]"
                >
                  <div className="bg-transparent rounded-xl border border-slate-800/20 shadow-lg shadow-black/10 overflow-hidden h-full relative flex-1 min-h-[200px] chart-container">
                    <TradingView
                      selectedSymbol={selectedSymbol?.name || 'ROLEX:16610'}
                      onSymbolChanged={handleSymbolSelect}
                      isTvLibraryReady={isTvLibraryReady}
                      chartType={activeChart}
                    />
                  </div>
                </div>

                {/* Bottom panel - Transactions (with automatic height) */}
                <div
                  ref={transactionContainerRef}
                  className="flex flex-col p-4 pt-2"
                >
                  <div className="bg-transparent rounded-xl border border-slate-800/20 shadow-lg shadow-black/10 flex flex-col">
                    <Transactions
                      selectedSymbol={selectedSymbol || { name: 'ROLEX:16610' }}
                    />
                  </div>
                </div>
              </Split>
            </div>

            {/* Right panel - Watchlist (unchanged) */}
            <div className="border-l border-slate-800/20 bg-transparent shadow-inner shadow-black/10 relative flex flex-col h-screen sticky top-0 right-0">
              <DashboardWatchlist
                selectedSymbol={selectedSymbol || { name: 'ROLEX:16610' }}
                onSymbolSelect={handleSymbolSelect}
                tvWidget={tvWidget}
                activeChart={activeChart}
                onChartTypeChange={handleChartTypeChange}
              />
            </div>
          </Split>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {isLoading && (
          <motion.div
            key="loading-overlay"
            className="fixed inset-0 bg-slate-950/95 backdrop-blur-md z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            <div className="flex flex-col items-center">
              <LoadingLogo />
              <motion.p
                className="text-blue-400 mt-4 font-medium"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.4 }}
              >
                Loading dashboard...
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add a sliding menu for more options */}
      {showMenu && (
        <div className="fixed bottom-16 right-0 w-48 bg-slate-900 border border-slate-800 rounded-tl-lg shadow-xl animate-slide-up z-50">
          <div className="p-2">
            <Button
              variant="ghost"
              className="flex items-center justify-start w-full px-3 py-2 text-sm text-slate-300 hover:bg-blue-600/20 hover:text-blue-400 rounded-md"
              onClick={() => {
                router.push('/security');
                setShowMenu(false);
              }}
            >
              <Shield className="h-4 w-4 mr-2" />
              Security
            </Button>
            <Button
              variant="ghost"
              className="flex items-center justify-start w-full px-3 py-2 text-sm text-slate-300 hover:bg-blue-600/20 hover:text-blue-400 rounded-md"
              onClick={() => {
                router.push('/terms');
                setShowMenu(false);
              }}
            >
              <FileText className="h-4 w-4 mr-2" />
              Terms
            </Button>
            <Button
              variant="ghost"
              className="flex items-center justify-start w-full px-3 py-2 text-sm text-slate-300 hover:bg-blue-600/20 hover:text-blue-400 rounded-md"
              onClick={() => {
                router.push('/privacy-policy');
                setShowMenu(false);
              }}
            >
              <Lock className="h-4 w-4 mr-2" />
              Privacy
            </Button>
            <Button
              variant="ghost"
              className="flex items-center justify-start w-full px-3 py-2 text-sm text-slate-300 hover:bg-blue-600/20 hover:text-blue-400 rounded-md"
              onClick={() => {
                router.push('/api-docs');
                setShowMenu(false);
              }}
            >
              <Code className="h-4 w-4 mr-2" />
              API Docs
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
